import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { SurfaceMap } from './surfaces';

export interface CollisionHit {
  /** С чем столкнулись */
  what: string;
  /** Скорость в момент удара, м/с */
  speed: number;
}

/**
 * Слой собственного корпуса робота. Камеры робота его не рендерят — настоящая
 * камера не видит саму себя, а нарисованный в 5 см от объектива корпус
 * читается оператором как препятствие. Вид инструктора слой включает.
 */
export const ROBOT_LAYER = 1;

/**
 * Мир: физика Rapier + сцена Three. Физика идёт фиксированным шагом 60 Гц,
 * рендер — с частотой кадров браузера.
 */
export class SimWorld {
  readonly scene = new THREE.Scene();
  readonly physics: RAPIER.World;
  readonly surfaces = new SurfaceMap();
  readonly sun: THREE.DirectionalLight;
  private events: RAPIER.EventQueue;
  private names = new Map<number, string>();
  /** Сбиваемые предметы: конусы и прочая мелочь, которую робот может снести */
  private props: Array<{ body: RAPIER.RigidBody; mesh: THREE.Object3D }> = [];

  constructor() {
    this.physics = new RAPIER.World({ x: 0, y: -9.81, z: 0 });
    this.physics.timestep = 1 / 60;
    this.events = new RAPIER.EventQueue(true);

    this.scene.background = new THREE.Color(0x93a6bd);
    this.scene.fog = new THREE.Fog(0x93a6bd, 60, 260);

    const hemi = new THREE.HemisphereLight(0xd8e6ff, 0x6b6357, 1.1);
    this.scene.add(hemi);
    this.sun = new THREE.DirectionalLight(0xfff3e0, 1.9);
    this.sun.position.set(40, 60, 25);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    const s = 60;
    this.sun.shadow.camera.left = -s;
    this.sun.shadow.camera.right = s;
    this.sun.shadow.camera.top = s;
    this.sun.shadow.camera.bottom = -s;
    this.sun.shadow.camera.far = 200;
    // Корпус робота невидим для его камер, но тень отбрасывать должен
    this.sun.shadow.camera.layers.enable(ROBOT_LAYER);
    this.scene.add(this.sun);
  }

  /** Статический бокс: и коллизия, и меш. rotY — поворот вокруг вертикали, tilt — уклон, рад. */
  addBox(
    name: string,
    pos: [number, number, number],
    size: [number, number, number],
    color: number,
    opts: { rotY?: number; tilt?: number; visible?: boolean } = {},
  ): RAPIER.Collider {
    const q = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(opts.tilt ?? 0, opts.rotY ?? 0, 0, 'YXZ'),
    );
    const body = this.physics.createRigidBody(
      RAPIER.RigidBodyDesc.fixed()
        .setTranslation(pos[0], pos[1], pos[2])
        .setRotation({ x: q.x, y: q.y, z: q.z, w: q.w }),
    );
    const col = this.physics.createCollider(
      RAPIER.ColliderDesc.cuboid(size[0] / 2, size[1] / 2, size[2] / 2).setFriction(0.9),
      body,
    );
    this.names.set(col.handle, name);

    if (opts.visible !== false) {
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(size[0], size[1], size[2]),
        new THREE.MeshLambertMaterial({ color }),
      );
      mesh.position.set(pos[0], pos[1], pos[2]);
      mesh.quaternion.copy(q);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.scene.add(mesh);
    }
    return col;
  }

  /**
   * Рельеф вдоль оси Z одним телом: профиль задаётся ломаной [z, высота].
   * Пандус, площадка и съезд собираются в единый меш, а не из отдельных плит —
   * на стыке двух коробок робот ловит выталкивание от двойного контакта
   * и залипает на гребне.
   */
  addRampStrip(
    name: string,
    xCenter: number,
    width: number,
    profile: Array<[number, number]>,
    color: number,
  ): void {
    const xL = xCenter - width / 2;
    const xR = xCenter + width / 2;
    const yBot = -0.3;
    const verts: number[] = [];
    for (const [z, y] of profile) {
      verts.push(xL, y, z, xR, y, z, xL, yBot, z, xR, yBot, z);
    }
    const idx: number[] = [];
    for (let i = 0; i < profile.length - 1; i++) {
      const a = i * 4;
      const b = (i + 1) * 4;
      idx.push(a + 0, a + 1, b + 1, a + 0, b + 1, b + 0); // верх
      idx.push(a + 2, b + 2, b + 3, a + 2, b + 3, a + 3); // низ
      idx.push(a + 0, b + 0, b + 2, a + 0, b + 2, a + 2); // левый борт
      idx.push(a + 1, a + 3, b + 3, a + 1, b + 3, b + 1); // правый борт
    }
    const last = (profile.length - 1) * 4;
    idx.push(0, 2, 3, 0, 3, 1);
    idx.push(last + 0, last + 3, last + 2, last + 0, last + 1, last + 3);

    const body = this.physics.createRigidBody(RAPIER.RigidBodyDesc.fixed());
    const col = this.physics.createCollider(
      RAPIER.ColliderDesc.trimesh(new Float32Array(verts), new Uint32Array(idx)).setFriction(0.9),
      body,
    );
    this.names.set(col.handle, name);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    const mesh = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color }));
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.scene.add(mesh);
  }

  /** Плоская заплатка покрытия: визуал + запись в карту материалов (без своей коллизии) */
  addSurfacePatch(surface: string, rect: [number, number, number, number], color: number, y = 0.012): void {
    this.surfaces.add({ rect, surface });
    const [x0, z0, x1, z1] = rect;
    const geo = new THREE.PlaneGeometry(x1 - x0, z1 - z0);
    const mesh = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color }));
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set((x0 + x1) / 2, y, (z0 + z1) / 2);
    mesh.receiveShadow = true;
    this.scene.add(mesh);
  }

  /**
   * Сбиваемый предмет: динамическое тело, которое робот может задеть и уронить.
   * Сбитый конус — самая честная обратная связь по габаритам, какую даёт полигон.
   */
  addProp(
    name: string,
    pos: [number, number, number],
    collider: RAPIER.ColliderDesc,
    mesh: THREE.Object3D,
    massKg: number,
  ): void {
    const body = this.physics.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(pos[0], pos[1], pos[2])
        .setLinearDamping(0.6)
        .setAngularDamping(0.6),
    );
    const col = this.physics.createCollider(collider.setMass(massKg).setFriction(0.7), body);
    this.names.set(col.handle, name);
    mesh.position.set(pos[0], pos[1], pos[2]);
    this.scene.add(mesh);
    this.props.push({ body, mesh });
  }

  nameOf(handle: number): string {
    return this.names.get(handle) ?? 'препятствие';
  }

  register(handle: number, name: string): void {
    this.names.set(handle, name);
  }

  /** Один физический шаг; возвращает начавшиеся контакты */
  step(): Array<[number, number]> {
    const started: Array<[number, number]> = [];
    this.physics.step(this.events);
    this.events.drainCollisionEvents((h1, h2, begun) => {
      if (begun) started.push([h1, h2]);
    });
    for (const p of this.props) {
      const t = p.body.translation();
      const r = p.body.rotation();
      p.mesh.position.set(t.x, t.y, t.z);
      p.mesh.quaternion.set(r.x, r.y, r.z, r.w);
    }
    return started;
  }
}
